import pluralize from "pluralize";
import {Link, useLocation} from "react-router";

import {CreateWithAssistantButton} from "@/components/assistant/CreateWithAssistantButton.tsx";
import {Button} from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {AnalyzerSource} from "@/lib/queries/analyzerRuns.ts";
import {MonitoringServiceType, Service, ServiceDefinition} from "@/lib/types/checks.ts";
import {DashboardDefinition, ServicePerformanceMetric} from "@/lib/types/website.ts";
import {CheckCard} from "@/pages/dashboard/display/components/checkCard/CheckCard.tsx";

interface DashboardChecksProps {
  dashboardDef: DashboardDefinition;
  services: Service[];
  servicesToShow: number;
  servicesTotal: number;
  setServicesToShow: (servicesToShow: number) => void;
  performanceMetrics: ServicePerformanceMetric[];
  serviceDefinitions: ServiceDefinition[];
}

export const DashboardChecks = ({
  dashboardDef,
  services,
  servicesToShow,
  servicesTotal,
  setServicesToShow,
  performanceMetrics,
  serviceDefinitions,
}: DashboardChecksProps) => {
  const location = useLocation();
  const {user} = useCurrentUserContext();

  const performanceMetricsByServiceId: Record<number, ServicePerformanceMetric> = performanceMetrics.reduce(
    (newObject, metric) => Object.assign(newObject, {[metric.service_id]: metric}),
    {} as Record<number, ServicePerformanceMetric>
  );

  const serviceDefinitionsByType: Record<MonitoringServiceType, ServiceDefinition> = serviceDefinitions.reduce(
    (newObject, serviceDefinition) => Object.assign(newObject, {[serviceDefinition.service_type]: serviceDefinition}),
    {} as Record<MonitoringServiceType, ServiceDefinition>
  );

  const recordsLeft =
    servicesTotal - servicesToShow < dashboardDef.services_num_to_show
      ? servicesTotal - servicesToShow
      : dashboardDef.services_num_to_show;

  return (
    <>
      <div className="d-flex justify-content-between">
        <div className="pulseTextH2Bold">Checks</div>
        <div className="d-flex align-items-center gap-2">
          <CreateWithAssistantButton state={{from: location}} source={AnalyzerSource.DASHBOARD_CHECKS_ACTION_BAR} />
          {user?.has_write_access && (
            <Button id="add-new-check" as={Link} to="/checks/create" variant="secondary" state={{from: location}}>
              Create Check
            </Button>
          )}
        </div>
      </div>
      <div
        id="dashboard-checks"
        className="row row-cols-sm-2 row-cols-xl-4 row-gap-4"
        data-testid="dashboard-checks-wrapper"
      >
        {services.length === 0 && <div className="pulseTextBodySmall">No Checks to display.</div>}
        {services.map((service) => (
          <CheckCard
            dashboardDef={dashboardDef}
            service={service}
            performanceMetric={performanceMetricsByServiceId[service.id]}
            serviceDefinition={serviceDefinitionsByType[service.monitoring_service_type]}
            key={service.id}
          />
        ))}
      </div>
      {recordsLeft > 0 && (
        <div className="d-flex justify-content-between align-items-center">
          <Button
            id="show-more-checks"
            variant="secondary"
            onClick={() => setServicesToShow(servicesToShow + dashboardDef.services_num_to_show)}
          >
            Show {recordsLeft} More {pluralize("Check", recordsLeft)}
          </Button>
        </div>
      )}
    </>
  );
};
